import React, { useEffect, useMemo, useState } from 'react';

import { Grid, IconButton, MenuItem, TextField, Tooltip, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import AssetRelationDialog from 'components/_dialogs/AssetRelationDialog/AssetRelationDialog';
import ColoredButton from 'components/ColoredButton';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import FooterButton from 'components/FooterButton';
import Table from 'components/Table';
import assetRelationsApi from 'config/api/assetRelations/assetRelations';
import { AssetRelation, AssetRelationCategory, AssetRelationType } from 'config/api/assetRelations/assetRelations.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import ROLES from 'config/constants/ROLES';
import getShortLanguage from 'helpers/getShortLanguage';
import useBoolState from 'hooks/useBoolState';
import usePermissions from 'hooks/usePermissions';
import assets_messages from 'messages/assets_messages';
import general_messages from 'messages/general_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import userDataStorage from 'storages/userDataStorage';
import { TableDataMappingRow } from 'types/Table';

type Props = {
  assetId?: number | string;
  assetName?: string;
  assetOwnerId?: number | string;
  descriptionPreviewLength?: number;
};

const PAGE_SIZE = 30;
const DEFAULT_DESCRIPTION_PREVIEW_LENGTH = 15;

const getDescriptionPreview = (description: string, previewLength: number) =>
  description.length > previewLength ? `${description.slice(0, previewLength)}...` : description;

const getRelationPerspective = (relation: AssetRelation, assetId?: number | string) => {
  if (assetId && String(relation.asset2.id) === String(assetId)) {
    return {
      asset1: relation.asset2,
      asset2: relation.asset1,
      direction: relation.relationType.direction2,
    };
  }

  return {
    asset1: relation.asset1,
    asset2: relation.asset2,
    direction: relation.relationType.direction1,
  };
};

const AssetRelationsTable: React.FC<Props> = ({
  assetId,
  assetName,
  assetOwnerId,
  descriptionPreviewLength = DEFAULT_DESCRIPTION_PREVIEW_LENGTH,
}) => {
  const { i18n, t } = useTranslation();
  const language = getShortLanguage(i18n.resolvedLanguage || i18n.language);
  const queryClient = useQueryClient();
  const [isAssetsManager = false] = usePermissions([ROLES.ORGANIZATION.ASSETS_MANAGER]) || [];
  const { showConfirmationModal } = useConfirmationModalContext();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [relationType, setRelationType] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<AssetRelation[]>([]);
  const [editingRelation, setEditingRelation] = useState<AssetRelation | null>(null);
  const { state: isDialogOpen, setTrue: openDialog, setFalse: closeDialog } = useBoolState(false);
  const currentUserId = userDataStorage.get()?.id;
  const isCurrentAssetOwner = Boolean(assetId && currentUserId != null && String(currentUserId) === String(assetOwnerId));
  const canCreateRelation = isAssetsManager || isCurrentAssetOwner;

  const { data: categories = [] } = useQuery<AssetRelationCategory[]>([QUERY_KEYS.ASSET_RELATIONS_GET_CATEGORIES, language], () =>
    assetRelationsApi.getCategories(language),
  );
  const { data: relationTypes = [] } = useQuery<AssetRelationType[]>([QUERY_KEYS.ASSET_RELATIONS_GET_TYPES, 'all', language], () =>
    assetRelationsApi.getTypes(undefined, language),
  );

  const params = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset,
      ...(search && { search }),
      ...(category && { category }),
      ...(relationType && { relation_type: relationType }),
      ...(assetId && { asset: assetId }),
    }),
    [assetId, category, offset, relationType, search],
  );

  const { data, isLoading, refetch } = useQuery([QUERY_KEYS.ASSET_RELATIONS_GET_LIST, params, language], () =>
    assetRelationsApi.getAll(params, language),
  );

  useEffect(() => {
    if (!data?.results) return;
    setRows(previous => (offset === 0 ? data.results : [...previous, ...data.results]));
  }, [data, offset]);

  useEffect(() => {
    setOffset(0);
  }, [search, category, relationType, assetId, language]);

  const filteredTypes = relationTypes.filter((type: AssetRelationType) => !category || type.categoryKey === category);

  const openCreate = () => {
    setEditingRelation(null);
    openDialog();
  };

  const openEdit = (relation: AssetRelation) => {
    setEditingRelation(relation);
    openDialog();
  };

  const removeRelation = async (relation: AssetRelation) => {
    const shouldProceed = await showConfirmationModal({
      title: t(assets_messages.relations.remove_title),
      body: t(assets_messages.relations.remove_body),
    });
    if (!shouldProceed) return;
    await assetRelationsApi.remove(relation.id);
    await queryClient.invalidateQueries(QUERY_KEYS.ASSET_RELATIONS_GET_LIST);
    refetch();
  };

  const canShowRowActions = (relation: AssetRelation) => isAssetsManager || (Boolean(assetId) && (relation.canEdit || relation.canDelete));

  const getRowActions = (relation: AssetRelation) =>
    [
      relation.canEdit ? { label: t(assets_messages.relations.edit), onClick: () => openEdit(relation) } : null,
      relation.canDelete ? { label: t(assets_messages.relations.delete), onClick: () => removeRelation(relation) } : null,
    ].filter(Boolean) as { label: string; onClick: () => void }[];

  const dataMapping = useMemo<TableDataMappingRow<AssetRelation>[]>(
    () => [
      {
        id: 'asset_1',
        width: '25%',
        label: t(assets_messages.relations.asset_1),
        get: relation => getRelationPerspective(relation, assetId).asset1.name,
      },
      {
        id: 'relation',
        width: '20%',
        label: t(assets_messages.relations.relation),
        get: relation => getRelationPerspective(relation, assetId).direction,
      },
      {
        id: 'asset_2',
        width: '25%',
        label: t(assets_messages.relations.asset_2),
        get: relation => getRelationPerspective(relation, assetId).asset2.name,
      },
      {
        id: 'description',
        width: rows.some(canShowRowActions) ? '24%' : '30%',
        label: t(assets_messages.relations.description),
        get: relation => (
          <Tooltip title={relation.description}>
            <Typography style={{ whiteSpace: 'pre-line' }}>
              {getDescriptionPreview(relation.description, descriptionPreviewLength)}
            </Typography>
          </Tooltip>
        ),
      },
      ...(rows.some(canShowRowActions)
        ? [
            {
              id: 'context',
              width: '6%',
              label: '',
              isAddon: true,
              blockSorting: true,
              get: (relation: AssetRelation) =>
                canShowRowActions(relation) && (
                  <ContextMenu elements={getRowActions(relation)} id={`asset-relation-${relation.id}`}>
                    <IconButton aria-label={t(assets_messages.relations.actions)}>
                      <MoreIcon />
                    </IconButton>
                  </ContextMenu>
                ),
            },
          ]
        : []),
    ],
    [assetId, descriptionPreviewLength, isAssetsManager, rows, t],
  );

  return (
    <>
      <Grid alignItems='center' container spacing={2}>
        <Grid item md={4} xs={12}>
          <TextField
            fullWidth
            label={t(assets_messages.relations.search)}
            onChange={event => setSearch(event.target.value)}
            value={search}
            variant='outlined'
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <TextField
            fullWidth
            label={t(assets_messages.relations.category)}
            onChange={event => setCategory(event.target.value)}
            select
            value={category}
            variant='outlined'
          >
            <MenuItem value=''>-</MenuItem>
            {categories.map((item: AssetRelationCategory) => (
              <MenuItem key={item.key} value={item.key}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item md={3} xs={12}>
          <TextField
            fullWidth
            label={t(assets_messages.relations.relation_type)}
            onChange={event => setRelationType(event.target.value)}
            select
            value={relationType}
            variant='outlined'
          >
            <MenuItem value=''>-</MenuItem>
            {filteredTypes.map((item: AssetRelationType) => (
              <MenuItem key={item.key} value={item.key}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {canCreateRelation && (
          <Grid item md={2} xs={12}>
            {/* @ts-ignore */}
            <ColoredButton customColor='secondary' onClick={openCreate} variant='outlined'>
              {t(assets_messages.relations.new_relation)}
            </ColoredButton>
          </Grid>
        )}
      </Grid>
      <Table data={rows} dataMapping={dataMapping} noDataMessage={t(assets_messages.relations.no_relations)} refetching={isLoading} />
      {data?.next && (
        // @ts-ignore
        <FooterButton loading={isLoading} onClick={() => setOffset(offset + PAGE_SIZE)}>
          {t(general_messages.show_more)}
        </FooterButton>
      )}
      {isDialogOpen && (
        <AssetRelationDialog
          fixedAssetId={assetId}
          fixedAssetName={assetName}
          onClose={closeDialog}
          open={isDialogOpen}
          relation={editingRelation}
        />
      )}
    </>
  );
};

export default AssetRelationsTable;
